import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight, Upload } from "lucide-react";
import { Badge } from "../lib/ui";
import EvidenceIntake from "../components/EvidenceIntake";
import ConnectorActionModal from "../components/ConnectorActionModal";
import DataRowTable from "../components/DataRowTable";
import RowActionBar from "../components/RowActionBar";
import ModalScrim from "../components/ModalScrim";
import StorySpine from "../components/StorySpine";
import SubFlowNav, { FocusPanel, MetricStrip, SimpleBars } from "../components/SubFlow";
import { subflowHref, useSubFlow } from "../lib/subflow";
import { profilePassport, useAppGo } from "../lib/story";
import { canWriteEvidence, navItemAllowed } from "../lib/master";
import { factsMatchingStates, parseStateFilter } from "../lib/actionLinks";
import { EVIDENCE_DRILL, EVIDENCE_FLOWS, EVIDENCE_FOCUS_DETAIL } from "../lib/evidenceFlows";
import { getConnectors } from "../lib/api";
import { intakeTabForChannel, replayContent } from "../lib/connectors";

const STATE_TONE = { KNOWN: "blue", VERIFIED: "green", UNKNOWN: "amber", CONFLICTING: "rose", STALE: "amber", INFERRED: "slate", PENDING_CONFIRMATION: "amber", NOT_APPLICABLE: "slate" };

export default function Evidence({ app, onAddEvidence, onIngest, user }) {
  const go = useAppGo(app?.id);
  const location = useLocation();
  const { focus } = useSubFlow("overview");
  const stateFilter = parseStateFilter(location.search);
  const [modal, setModal] = useState(false);
  const [intakePreset, setIntakePreset] = useState(null);
  const [connectorModal, setConnectorModal] = useState(null);
  const [connectors, setConnectors] = useState([]);
  const [lastImport, setLastImport] = useState(null);
  const [viewFact, setViewFact] = useState(null);

  useEffect(() => {
    getConnectors()
      .then((r) => setConnectors(r.data?.connectors || []))
      .catch(() => setConnectors([]));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const connectorId = params.get("connector");
    const shouldOpen = params.get("open") === "intake";
    if (!connectorId || !shouldOpen || !app?.id) return;
    getConnectors()
      .then((r) => {
        const match = (r.data?.connectors || []).find((c) => c.id === connectorId);
        if (match) setConnectorModal(connectorId);
      })
      .catch(() => {});
  }, [app?.id, location.search]);

  const facts = app?.evidence?.facts || [];
  const filteredFacts = factsMatchingStates(facts, stateFilter);
  const displayFacts = stateFilter?.length ? filteredFacts : facts;

  const closeFactView = useCallback((event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setViewFact(null);
  }, []);

  if (!app) return null;
  const isOverview = focus === "overview";
  const mayIngest = canWriteEvidence(user?.guide);
  const mayAssess = navItemAllowed("/assessment", user?.guide);
  const counts = app.evidence?.counts || {};
  const openQuestions = (app.questions || []).filter(q => q.status === "OPEN");
  const sourceConnectors = connectors.filter((c) => c.kind === "source");

  const afterIngest = (result) => setLastImport(result?.stats || { imported: 1 });

  const openImportReplay = ({ replay }) => {
    const { content, filename } = replayContent(replay);
    setIntakePreset({
      tab: intakeTabForChannel(replay.channel),
      content,
      filename,
      source: replay.source_label,
    });
    setConnectorModal(null);
    setModal(true);
  };

  const qualityRows = [
    { label: "Verified", value: counts.verified || 0, tone: "green", testId: "evidence-count-verified" },
    { label: "Known", value: counts.known || 0, tone: "blue", testId: "evidence-count-known" },
    { label: "Unknown", value: counts.unknown || 0, tone: "amber", testId: "evidence-count-unknown" },
    { label: "Conflicting", value: counts.conflicting || 0, tone: "amber", testId: "evidence-count-conflicting" },
  ];

  return (
    <div className="inner-page subflow-page">
      {!isOverview && (
        <Link to={subflowHref("/evidence", app.id, "overview")} className="analytics-back" data-testid="evidence-back">
          <ChevronLeft size={16} /> Back to evidence overview
        </Link>
      )}

      <FocusPanel
        kicker={`EVIDENCE · ${app.id}`}
        title={isOverview ? "Canonical facts" : (EVIDENCE_FLOWS.find(f => f.id === focus)?.label || "Evidence")}
        detail={EVIDENCE_FOCUS_DETAIL[focus] || "UNKNOWN is never coerced to yes/no."}
        testId="evidence-head"
      />

      {!isOverview && (
        <>
          <SubFlowNav items={EVIDENCE_FLOWS} focus={focus} appId={app.id} testId="evidence-subflow" />
          <StorySpine current="assess" compact />
        </>
      )}

      {isOverview && (
        <>
          <MetricStrip items={[
            { label: "Coverage", value: `${app.evidence?.coverage || 0}%`, testId: "evidence-overview-coverage" },
            { label: "Facts", value: facts.length, testId: "evidence-overview-facts" },
            { label: "Unknown", value: counts.unknown || 0, testId: "evidence-overview-unknown" },
            { label: "Questions", value: openQuestions.length, testId: "evidence-overview-questions" },
          ]} />
          <section className="feature-panel analytics-panel" data-testid="evidence-drill">
            <p className="kicker">DRILL DOWN · ONE TOPIC PER SCREEN</p>
            <div className="lifecycle-phase-links">
              {EVIDENCE_DRILL.map(({ id, label }) => (
                <Link key={id} to={subflowHref("/evidence", app.id, id)} className="lifecycle-phase-link" data-testid={`evidence-drill-${id}`}>
                  <strong>{label}</strong>
                  <span>{EVIDENCE_FOCUS_DETAIL[id]}</span>
                  <ChevronRight size={16} />
                </Link>
              ))}
            </div>
          </section>
        </>
      )}

      {lastImport && (
        <div className="decision-input-banner ready" data-testid="ingest-result">
          <strong>{lastImport.imported || 0} imported · {lastImport.updated || 0} updated</strong>
        </div>
      )}

      {focus === "discovery" && (
        <FocusPanel
          kicker="G1 DISCOVERY · EVIDENCE"
          title="Minimum sufficient evidence"
          detail="Progressively enrich the application passport — not a disconnected questionnaire."
          testId="evidence-discovery-panel"
        >
          <p className="muted small-note">
            Run in parallel with pains and drivers. Canonical facts feed WHY and G1 readiness.
          </p>
          <MetricStrip items={[
            { label: "Coverage", value: `${app.evidence?.coverage || 0}%`, testId: "discovery-coverage" },
            { label: "Facts", value: facts.length, testId: "discovery-facts" },
            { label: "Open questions", value: openQuestions.length, testId: "discovery-questions" },
          ]} />
          <div className="passport-grid compact">
            {profilePassport(app.passport).slice(0, 4).map(([key, val]) => (
              <div key={key}><small>{key}</small><p>{val || "—"}</p></div>
            ))}
          </div>
          <div className="focus-actions">
            {mayIngest && (
              <button data-testid="discovery-open-ingest" className="primary-button" type="button" onClick={() => setModal(true)}>
                <Upload size={15} /> Ingest evidence
              </button>
            )}
            {mayAssess && (
              <>
                <button type="button" className="secondary-button" onClick={() => go("/assessment?focus=pain")}>Pains</button>
                <button type="button" className="secondary-button" onClick={() => go("/assessment?focus=why")}>Drivers / WHY</button>
              </>
            )}
          </div>
        </FocusPanel>
      )}

      {focus === "quality" && (
        <FocusPanel kicker="EVIDENCE QUALITY" title="State of the ledger" testId="evidence-quality-panel">
          <MetricStrip items={[
            { label: "Coverage", value: `${app.evidence?.coverage || 0}%`, testId: "metric-coverage" },
            { label: "Unknown", value: counts.unknown || 0, detail: "Blocks if HIGH", testId: "evidence-count-unknown" },
            { label: "Conflicting", value: counts.conflicting || 0, testId: "evidence-count-conflicting" },
            { label: "Open questions", value: openQuestions.length, testId: "metric-questions" },
          ]} />
          <SimpleBars rows={qualityRows} testId="evidence-quality-bars" />
          <div className="focus-actions">
            {mayIngest && <button data-testid="open-evidence-modal" className="primary-button" type="button" onClick={() => setModal(true)}><Upload size={15} /> Ingest</button>}
            {mayAssess && <button type="button" className="secondary-button" onClick={() => go("/assessment?focus=readiness")}>Assessment readiness</button>}
          </div>
        </FocusPanel>
      )}

      {focus === "facts" && (
        <FocusPanel kicker="FACT LEDGER" title={`${displayFacts.length} canonical facts`} testId="fact-table">
          {stateFilter?.length && (
            <p className="muted small-note" data-testid="evidence-state-filter">
              Showing {stateFilter.join(", ")} facts for this queue item ({displayFacts.length} of {facts.length}).
            </p>
          )}
          <DataRowTable
            testId="fact-ledger-table"
            columns={[
              { key: "type", label: "Fact", render: (f) => <><b>{f.type || f.label}</b> <em className="muted">{f.id}</em></> },
              { key: "value", label: "Value" },
              { key: "state", label: "State", render: (f) => <Badge tone={STATE_TONE[f.state] || "slate"}>{f.state}</Badge> },
              { key: "source", label: "Source", render: (f) => f.source || "—" },
            ]}
            rows={displayFacts}
            rowKey="id"
            pageSize={10}
            hint="Always-visible View — inspect provenance and impact before authorization."
            renderActions={(row, stableId) => (
              <RowActionBar
                testId={`fact-actions-${stableId}`}
                rowId={stableId}
                view
                review={row.state === "UNKNOWN" || row.state === "CONFLICTING"}
                onView={() => setViewFact(row)}
                onReview={() => setViewFact(row)}
              />
            )}
          />
        </FocusPanel>
      )}

      {focus === "sources" && (
        <FocusPanel
          kicker="CONNECTORS"
          title="Import replay is Now · live connectors are Next"
          detail="Manual, CSV, JSON and document ingest work today with full provenance."
          testId="connector-wall"
        >
          <p className="muted small-note">
            POC does not fake live CMDB or cloud pulls. Export from your toolchain, import here with source and observed date.
          </p>
          <div className="connector-chips">
            {(sourceConnectors.length ? sourceConnectors : [{ id: "cmdb", label: "CMDB", status: "stub", note: "CSV ingest available." }]).map((c) => (
              <button
                key={c.id}
                type="button"
                className="connector-chip connector-chip-action"
                data-testid={`connector-${c.id}`}
                onClick={() => setConnectorModal(c.id)}
              >
                <b>{c.label}</b>
                <small>{c.detail || c.note}</small>
                <Badge tone={c.live ? "green" : "amber"}>{c.live ? "Live" : "Import replay"}</Badge>
              </button>
            ))}
          </div>
          {mayIngest && (
            <div className="focus-actions">
              <button type="button" className="primary-button" data-testid="sources-open-ingest" onClick={() => setModal(true)}>
                <Upload size={15} /> Manual / file ingest
              </button>
            </div>
          )}
          <div className="passport-grid compact">
            {profilePassport(app.passport).slice(0, 6).map(([key, val]) => (
              <div key={key}><small>{key}</small><p>{val || "—"}</p></div>
            ))}
          </div>
        </FocusPanel>
      )}

      {mayIngest && modal && (
        <EvidenceIntake
          appId={app.id}
          preset={intakePreset}
          onClose={() => { setModal(false); setIntakePreset(null); }}
          onManual={onAddEvidence}
          onIngest={(p) => onIngest({
            ...p,
            source: p.source || intakePreset?.source || p.filename,
          }).then(afterIngest)}
        />
      )}

      {connectorModal && (
        <ConnectorActionModal
          connectorId={connectorModal}
          appId={app.id}
          onClose={() => setConnectorModal(null)}
          onImportReplay={openImportReplay}
        />
      )}

      {viewFact && (
        <ModalScrim testId={`fact-view-${viewFact.id}`} onBackdropClick={closeFactView}>
          <div className="modal wide" role="dialog" aria-modal="true" aria-labelledby="fact-detail-title" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="modal-close" onClick={closeFactView} aria-label="Close" data-testid="fact-view-close-x">×</button>
            <p className="kicker">FACT DETAIL · {viewFact.id}</p>
            <h2 id="fact-detail-title">{viewFact.type || viewFact.label}</h2>
            <dl className="admin-review-dl">
              {[["Value", viewFact.value], ["State", viewFact.state], ["Source", viewFact.source], ["Observed", viewFact.observed], ["Impact", viewFact.impact]].map(([label, val]) => (
                val != null && String(val).trim() !== "" && (
                  <div key={label} className="admin-review-row"><dt>{label}</dt><dd>{String(val)}</dd></div>
                )
              ))}
            </dl>
            <button type="button" className="primary-button" onClick={closeFactView} data-testid="fact-view-close">Close</button>
          </div>
        </ModalScrim>
      )}
    </div>
  );
}
